/**
 * Step-and-repeat imposition onto a press sheet (SRA3 by default): pieces are
 * the trimmed size plus bleed, butted together with no gap, the block centred
 * on the sheet, no crop marks. The sheet orientation giving the most pieces wins.
 * Examples (3 mm bleed): DL 105×216 → 6-up, A5 154×216 → 4-up,
 * A4 216×303 → 2-up, A3 303×426 → 1-up.
 */
export interface PressSheet {
  name: string;
  w: number;
  h: number;
}
export const SRA3: PressSheet = { name: 'SRA3', w: 320, h: 450 };

export interface Imposition {
  sheet: PressSheet;
  /** imposed sheet size as output (may be landscape) */
  sheetW: number;
  sheetH: number;
  pieceW: number;
  pieceH: number;
  cols: number;
  rows: number;
  perSheet: number;
  /** left/top offset of the centred block, mm */
  offsetX: number;
  offsetY: number;
}

export function computeImposition(trimW: number, trimH: number, bleed: number, sheet: PressSheet = SRA3): Imposition | null {
  if (bleed <= 0) return null; // no-bleed jobs print actual size, uncut
  return computeImpositionForPiece(trimW + 2 * bleed, trimH + 2 * bleed, sheet);
}

/** Imposition of a piece of a given size (trim + bleed where it has bleed, e.g. a DL tent 216 × 315). */
export function computeImpositionForPiece(pw: number, ph: number, sheet: PressSheet = SRA3): Imposition | null {
  let best: Imposition | null = null;
  for (const [sw, sh] of [[sheet.w, sheet.h], [sheet.h, sheet.w]]) {
    const cols = Math.floor(sw / pw + 1e-9), rows = Math.floor(sh / ph + 1e-9);
    const n = cols * rows;
    if (n > 0 && (!best || n > best.perSheet))
      best = { sheet, sheetW: sw, sheetH: sh, pieceW: pw, pieceH: ph, cols, rows, perSheet: n, offsetX: (sw - cols * pw) / 2, offsetY: (sh - rows * ph) / 2 };
  }
  return best;
}

/**
 * Which page goes into each position of each sheet.
 * - ganged: consecutive pages fill the positions in reading order (left→right,
 *   top→bottom); the last sheet may have empty positions (-1).
 * - repeat: every position of a sheet carries the same page (one sheet per page).
 */
export function sheetLayout(pageCount: number, perSheet: number, mode: 'ganged' | 'repeat'): number[][] {
  if (mode === 'repeat') return Array.from({ length: pageCount }, (_, p) => Array(perSheet).fill(p));
  const sheets: number[][] = [];
  for (let start = 0; start < pageCount; start += perSheet)
    sheets.push(Array.from({ length: perSheet }, (_, i) => (start + i < pageCount ? start + i : -1)));
  return sheets;
}

/** Position (mm, top-left origin) of slot i on the imposed sheet. */
export function slotPosition(imp: Imposition, i: number): { x: number; y: number } {
  const c = i % imp.cols, r = Math.floor(i / imp.cols);
  return { x: imp.offsetX + c * imp.pieceW, y: imp.offsetY + r * imp.pieceH };
}

export interface ImposedSheet {
  /** page index per slot (-1 = empty) */
  slots: number[];
  /** back of a double-sided sheet: pages are rotated 180° (head-to-foot) */
  back: boolean;
}

/**
 * Double-sided leaves (pages in press order, taken in pairs: front, back — see
 * printOrder). Each front sheet is
 * followed by its back sheet. The press sheet is turned left-to-right, so each
 * row's columns are mirrored on the back; backs are also turned 180° so a
 * top-bound leaf reads upright when flipped over the binding (tumble).
 */
export function duplexSheets(pageCount: number, imp: Pick<Imposition, 'cols' | 'rows' | 'perSheet'>): ImposedSheet[] {
  const leaves = Math.ceil(pageCount / 2);
  const out: ImposedSheet[] = [];
  for (let start = 0; start < leaves; start += imp.perSheet) {
    const front: number[] = [], back: number[] = Array(imp.perSheet).fill(-1);
    for (let i = 0; i < imp.perSheet; i++) {
      const leaf = start + i;
      front.push(leaf < leaves ? leaf * 2 : -1);
      const c = i % imp.cols, r = Math.floor(i / imp.cols);
      const mirror = r * imp.cols + (imp.cols - 1 - c);
      back[mirror] = leaf < leaves && leaf * 2 + 1 < pageCount ? leaf * 2 + 1 : -1;
    }
    out.push({ slots: front, back: false }, { slots: back, back: true });
  }
  return out;
}
